import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  Headphones,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Mic,
  Target,
  HeartHandshake,
  BarChart3,
  Brain,
  Users,
  Zap,
  ChevronRight,
} from "lucide-react";

const KEY_TOPICS = [
  "Transition from Individual Contributor to Supervisor",
  "Goal Setting & Performance Management",
  "Coaching for Performance",
  "Feedback & Accountability",
  "Employee Engagement & Retention",
  "Difficult Conversations",
  "Conflict Management",
  "Stakeholder Management",
  "Leading Through Change",
  "Team Meetings & Communication",
  "Delivering Results Through Others",
  "Future Readiness & Growth",
  "Executive Presence & Leadership Presence",
  "Business Acumen & Commercial Thinking",
  "Supervisor Mindset, Confidence & Self-Leadership",
];

const SCENARIO_TEASERS = [
  { tag: "Performance", title: "Give feedback that lands", icon: Target, color: "orange" },
  { tag: "Career Coaching", title: "Coach an ambitious rep", icon: Sparkles, color: "blue" },
  { tag: "Self-awareness", title: "Hear hard feedback yourself", icon: ShieldCheck, color: "orange" },
  { tag: "Retention", title: "Save a flight-risk star", icon: HeartHandshake, color: "blue" },
];

const STATS = [
  { value: "130+", label: "Practice conversations" },
  { value: "15", label: "Supervisory themes" },
  { value: "6", label: "Leadership competencies" },
  { value: "AI", label: "Powered coaching" },
];

const COMPETENCIES = [
  { icon: Brain, label: "Managing Others", pct: 78 },
  { icon: MessageSquareText, label: "Communication", pct: 85 },
  { icon: Users, label: "Motivating Others", pct: 71 },
  { icon: Zap, label: "Action Oriented", pct: 90 },
  { icon: BarChart3, label: "Delivering Results", pct: 82 },
  { icon: HeartHandshake, label: "Coaching & Developing", pct: 68 },
];

const PHOTO_HERO = "https://images.unsplash.com/photo-1573497491208-6b1acb260507?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900";
const PHOTO_TEAM = "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900";
const PHOTO_PRESENTING = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900";

// Persona headshots for social proof strip (diverse professionals)
const FACE_PHOTOS = [
  { url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=200", label: "Sales Lead" },
  { url: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=200", label: "Operations" },
  { url: "https://images.unsplash.com/photo-1656399910161-8efafd973a29?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=200", label: "Eng Manager" },
  { url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=200", label: "HR Director" },
  { url: "https://images.unsplash.com/photo-1787724779241-cb5c250ed70b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=200", label: "Team Lead" },
];

export default function LandingPage() {
  const loginUrl = typeof window !== "undefined" ? `${window.location.origin}/login` : "/login";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-white">
        {/* subtle gradient blobs */}
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-orange-100/60 blur-3xl -z-10" aria-hidden />
        <div className="absolute top-48 -left-24 w-[400px] h-[400px] rounded-full bg-blue-100/50 blur-3xl -z-10" aria-hidden />

        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 md:pt-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* Left copy */}
            <div className="lg:col-span-6 xl:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-200 px-4 py-1.5 text-xs tracking-[0.18em] uppercase text-orange-700 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                LeaderSim · by Emergence · for managers
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
                Practice the conversation.
                <br />
                <span className="text-[#EA580C]">Master the moment.</span>
              </h1>

              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                A private practice studio for managers — rehearse the hardest people
                conversations with an AI that pushes back like a real direct report.
                Get scored, get coached, walk in ready.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/login">
                  <Button
                    data-testid="hero-cta-login"
                    className="rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 h-12 text-base font-semibold shadow-lg shadow-orange-200"
                  >
                    Sign in
                    <ArrowUpRight className="w-4 h-4 ml-1.5" strokeWidth={2} />
                  </Button>
                </Link>
              </div>

              {/* Feature chips */}
              <div className="mt-9 flex items-center gap-5 text-sm text-slate-500 flex-wrap">
                {[
                  { icon: Mic, text: "Voice in & out" },
                  { icon: MessageSquareText, text: "Live coach hints" },
                  { icon: Headphones, text: "Natural English voice" },
                  { icon: ShieldCheck, text: "Private & secure" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-blue-500" strokeWidth={1.75} />
                    {text}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: image + preview card */}
            <div className="lg:col-span-6 xl:col-span-5 relative">
              {/* Photo */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200 aspect-[4/3]">
                <img
                  src={PHOTO_HERO}
                  alt="Professional coaching conversation"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
              </div>

              {/* Floating conversation card */}
              <div className="absolute -bottom-6 -left-4 sm:-left-8 w-72 bg-white border border-slate-200 rounded-2xl p-4 shadow-xl">
                <div className="space-y-2.5">
                  <div className="bg-slate-100 text-slate-800 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm leading-relaxed">
                    Everything's fine — just a lot on the plate.
                  </div>
                  <div className="bg-[#2563EB] text-white px-4 py-2.5 rounded-2xl rounded-tr-sm text-sm leading-relaxed ml-4">
                    I noticed Tuesday's deadline slipped. What's really going on?
                  </div>
                  <div className="bg-orange-50 border border-orange-200 text-orange-900 px-3 py-2 rounded-xl text-xs flex gap-2 items-start">
                    <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-500" strokeWidth={1.75} />
                    Strong opener — stay quiet, let silence work.
                  </div>
                </div>
              </div>

              {/* Floating score badge */}
              <div className="absolute -top-4 -right-4 bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-xl border border-slate-800">
                <div className="text-[9px] tracking-[0.18em] uppercase text-slate-400">Coach score</div>
                <div className="font-display text-2xl font-bold text-[#EA580C]">
                  78<span className="text-slate-500 text-base font-normal"> / 100</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="border-y border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-slate-200">
            {STATS.map((s) => (
              <div key={s.label} className="text-center md:px-8">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#EA580C]">{s.value}</div>
                <div className="text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SCENARIO BENTO ── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">Practice library</div>
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 tracking-tight font-bold">
              The people conversations every manager dreads.
            </h2>
            <p className="mt-3 text-slate-600">
              Burnout, feedback, defensiveness, succession, flight risks — built around the actual moments managers face every week.
            </p>
          </div>
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1.5 font-medium">
            Browse all scenarios <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SCENARIO_TEASERS.map((s, i) => {
            const Icon = s.icon;
            const isOrange = s.color === "orange";
            return (
              <div
                key={i}
                className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-transparent transition-all duration-300 slide-up-fade"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className={`w-11 h-11 rounded-xl grid place-items-center mb-5 transition-colors ${
                  isOrange
                    ? "bg-orange-50 group-hover:bg-orange-100"
                    : "bg-blue-50 group-hover:bg-blue-100"
                }`}>
                  <Icon className={`w-5 h-5 ${isOrange ? "text-[#EA580C]" : "text-[#2563EB]"}`} strokeWidth={1.75} />
                </div>
                <div className={`text-[10px] font-semibold tracking-widest uppercase mb-1.5 ${isOrange ? "text-orange-600" : "text-blue-600"}`}>
                  {s.tag}
                </div>
                <div className="font-semibold text-slate-900 leading-tight">{s.title}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── PHOTO + COMPETENCIES SPLIT ── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Photo */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-video lg:aspect-[4/3]">
            <img
              src={PHOTO_TEAM}
              alt="Diverse leadership team meeting"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-transparent to-orange-900/20" />
            <div className="absolute bottom-5 left-5 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3">
              <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Get assessed on</div>
              <div className="text-slate-900 font-bold text-sm mt-0.5">6 Leadership Competencies</div>
            </div>
          </div>

          {/* Competency bars */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-orange-600 mb-3">Your scorecard</div>
            <h2 className="font-display text-2xl md:text-3xl text-slate-900 font-bold mb-2">
              Know exactly where you stand.
            </h2>
            <p className="text-slate-600 mb-8">
              After every practice session you get scored across 6 core leadership competencies — with quoted moments and what to try next time.
            </p>
            <div className="space-y-4">
              {COMPETENCIES.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
                        <span className="text-slate-700 font-medium">{c.label}</span>
                      </div>
                      <span className="font-semibold text-slate-900">{c.pct}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#EA580C] to-[#2563EB]"
                        style={{ width: `${c.pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── KEY TOPICS ── */}
      <section className="bg-slate-50 border-y border-slate-100" data-testid="key-topics-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">Key topics</div>
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 tracking-tight font-bold">
              15 supervisory themes. 130 practice conversations.
            </h2>
            <p className="mt-3 text-slate-600">
              From your first day leading former peers to coaching, feedback, conflict and executive presence — every theme a new supervisor needs to master.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {KEY_TOPICS.map((t, i) => (
              <Link
                key={t}
                to={`/scenarios?category=${encodeURIComponent(t)}`}
                data-testid={`key-topic-${i + 1}`}
                className="group flex items-center gap-4 bg-white border border-slate-200 rounded-xl px-5 py-4 hover:border-orange-300 hover:shadow-md transition-all"
              >
                <span className="font-display text-base text-orange-200 group-hover:text-orange-400 tabular-nums w-7 shrink-0 transition-colors font-bold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-medium text-slate-800 leading-snug flex-1 text-sm">{t}</span>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-400 transition-colors shrink-0" strokeWidth={1.75} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="text-center mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest text-orange-600 mb-3">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold">Three steps to confidence.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              num: "01",
              title: "Pick a scenario",
              body: "Choose from burnout, defensive feedback, leading former peers, flight risks and more — each grounded in a real manager moment.",
              color: "orange",
            },
            {
              num: "02",
              title: "Have the conversation",
              body: "Speak or type in English. The AI direct report pushes back like a real person — including shutting down if you're vague.",
              color: "blue",
            },
            {
              num: "03",
              title: "Get coached, walk in ready",
              body: "Live hints during the call, then a full scorecard: empathy, clarity, assertiveness, listening — plus quoted moments and what to try next.",
              color: "orange",
            },
          ].map((step, i) => (
            <div
              key={i}
              className="relative bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 slide-up-fade"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className={`font-display text-5xl font-bold mb-4 ${step.color === "orange" ? "text-orange-100" : "text-blue-100"}`}>
                {step.num}
              </div>
              <div className={`w-1 h-6 rounded-full mb-4 ${step.color === "orange" ? "bg-[#EA580C]" : "bg-[#2563EB]"}`} />
              <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOCIAL PROOF PHOTO STRIP ── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-stretch">
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden min-h-[280px]">
            <img
              src={PHOTO_PRESENTING}
              alt="Manager leading a team presentation"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 to-transparent flex items-end p-8">
              <div>
                <p className="text-white font-display text-xl md:text-2xl font-bold max-w-xs leading-snug">
                  "The practice made the real thing feel easy."
                </p>
                <p className="text-white/60 text-sm mt-2">— Participant, Batch 1</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-rows-2 gap-5">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-6 flex flex-col justify-center">
              <BarChart3 className="w-8 h-8 text-orange-500 mb-3" strokeWidth={1.5} />
              <div className="font-bold text-slate-900 text-lg">Detailed analytics</div>
              <p className="text-slate-600 text-sm mt-1">Track progress across every competency session by session.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-6 flex flex-col justify-center">
              <Brain className="w-8 h-8 text-blue-500 mb-3" strokeWidth={1.5} />
              <div className="font-bold text-slate-900 text-lg">AI coaching report</div>
              <p className="text-slate-600 text-sm mt-1">14-section coaching report with quoted evidence from your conversation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FACE STRIP — "Built for working managers" ── */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 pb-16">
        <div className="bg-slate-50 border border-slate-200 rounded-3xl px-8 py-8 flex flex-col sm:flex-row items-center gap-8">
          {/* Stacked avatars */}
          <div className="flex -space-x-3 shrink-0">
            {FACE_PHOTOS.map((f, i) => (
              <div
                key={i}
                className="w-12 h-12 rounded-full border-2 border-white overflow-hidden shadow-sm ring-2 ring-white"
                style={{ zIndex: FACE_PHOTOS.length - i }}
              >
                <img src={f.url} alt={f.label} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
          {/* Copy */}
          <div className="flex-1 text-center sm:text-left">
            <div className="font-display text-xl font-bold text-slate-900">Built for working managers.</div>
            <p className="text-slate-500 mt-1 text-sm">Sales leads, engineering managers, HR directors, operations supervisors — all practising the conversations that matter.</p>
          </div>
          {/* CTA */}
          <Link to="/login" className="shrink-0">
            <Button className="rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white px-6 h-11 font-semibold shadow-sm shadow-orange-100 whitespace-nowrap">
              Start practising
            </Button>
          </Link>
        </div>
      </section>

      {/* ── QR CODE ── */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-center md:text-left">
              <div className="text-xs font-semibold uppercase tracking-widest text-orange-400 mb-3">Quick access</div>
              <h2 className="font-display text-2xl md:text-3xl text-white font-bold">Scan to Access LeaderSim</h2>
              <p className="mt-3 text-slate-400 max-w-sm">
                Share this QR code with your team for instant access to the LeaderSim practice portal.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm text-slate-400 justify-center md:justify-start">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Direct link to login page
              </div>
            </div>

            {/* QR code box */}
            <div className="flex flex-col items-center gap-4">
              <div
                className="bg-white rounded-2xl p-5 shadow-2xl border-4 border-[#EA580C]"
                data-testid="qr-code-box"
              >
                <QRCodeSVG
                  value={loginUrl}
                  size={160}
                  fgColor="#0F172A"
                  bgColor="#FFFFFF"
                  level="M"
                  marginSize={0}
                />
              </div>
              <div className="text-center">
                <div className="text-white font-semibold text-sm">Scan to Access LeaderSim</div>
                <div className="text-slate-400 text-xs mt-1">Works on any mobile device</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
        <div className="relative bg-gradient-to-br from-[#EA580C] to-[#C2410C] text-white rounded-3xl overflow-hidden p-10 md:p-16">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" aria-hidden />
          <div className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full bg-black/10 blur-3xl" aria-hidden />
          <div className="relative">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight max-w-2xl">
              The next hard conversation won't be your first attempt.
            </h2>
            <p className="text-orange-100 mt-5 max-w-xl text-lg">
              Rehearse it. Get a score. Walk in ready.
            </p>
            <Link to="/login">
              <Button
                data-testid="footer-cta-login"
                className="mt-8 rounded-xl bg-white text-[#EA580C] hover:bg-orange-50 px-8 h-12 text-base font-bold shadow-lg"
              >
                Sign in
                <ArrowUpRight className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_roleplay-mentor-3/artifacts/lkmqxka2_Emergence%20Logo%20Black%2080x265-%20Original.jpg"
              alt="Emergence"
              className="h-8 w-auto object-contain"
              data-testid="footer-emergence-logo"
            />
            <div className="leading-tight">
              <div className="font-display text-sm font-bold text-slate-900">
                Leader<span className="text-[#EA580C]">Sim</span>
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                Create. Connect. Transform.
              </div>
            </div>
          </div>
          <div className="text-sm text-slate-500 md:text-center">
            © {new Date().getFullYear()} Emergence
          </div>
          <div className="text-xs text-slate-500 md:text-right">
            Voice powered by Whisper &amp; ElevenLabs · Coaching by Claude
          </div>
        </div>
      </footer>
    </div>
  );
}
